import * as THREE from 'three';
import { recordingsOf } from '../audio/files.js';
import { HALOS_PER_WORLD, HaloField } from '../look/halos.js';
import { skyParts, type FloatImage, type SkyParts } from '../look/sky-parts.js';
import { directionFrom, makeSun } from '../look/sun.js';
import { WorldLoader } from './loader.js';
import { MANIFEST, hdriFile, worldUrl, type Manifest } from './manifest.js';
import { WorldBuilder, type Fetchers, type WorldDefinition } from './world.js';

/** A world, built and filling in (ADR-032). */
export type BuiltWorld = {
  readonly definition: WorldDefinition;
  readonly builder: WorldBuilder;
  readonly root: THREE.Group;
  readonly sun: THREE.DirectionalLight;
  readonly halos: HaloField;
  /** From the stage towards the sun, in the room. */
  readonly towardsSun: THREE.Vector3;
  readonly loader: WorldLoader;
  /** `held` while the view is black at a fade: the features wait (onFeatureFrame). */
  update(dt: number, elapsed: number, camera: THREE.Camera, held?: boolean): void;
  /** Its features' states, as the lesson has them at a step (lesson-schema's worldStateAt). */
  setStates(states: ReadonlyMap<string, string>, animate: boolean): void;
  dispose(): void;
};

/** Where a world's sun is: as it says, or its sky's own, turned with the sky. */
export function sunOf(definition: WorldDefinition, manifest: Manifest = MANIFEST): THREE.Vector3 {
  if (definition.sun.from)
    return directionFrom(definition.sun.from.azimuth, definition.sun.from.elevation);
  const measured = hdriFile(definition.sky.hdri, manifest).sun?.direction ?? [0, 1, 0];
  return new THREE.Vector3(...measured)
    .applyEuler(new THREE.Euler(0, THREE.MathUtils.degToRad(definition.sky.yaw), 0))
    .normalize();
}

/** How far ahead of a lesson's see-through things the world's are drawn (renderOrder). */
const WORLD_FIRST = 10;

export function buildWorld(
  definition: WorldDefinition,
  options: {
    fetchers?: Fetchers;
    loader?: WorldLoader;
    manifest?: Manifest;
    /** Where a stored file is fetched from; `worldUrl` for the code worlds. */
    urlOf?: (stored: string) => string;
    /**
     * Makes the sky's parts from its float image as part of loading it: by
     * default on the page, the look has the pano worker do it (look/sky.ts).
     */
    prepareSky?: (image: FloatImage) => Promise<SkyParts>;
    /** The sky's parts when they are ready, or undefined when the sky cannot be had. */
    onSky?: (sky: SkyParts | undefined) => void;
  } = {},
): BuiltWorld {
  const loader = options.loader ?? new WorldLoader();
  const manifest = options.manifest ?? MANIFEST;
  const urlOf = options.urlOf ?? ((stored: string) => worldUrl(stored));
  const builder = new WorldBuilder(
    definition.slug,
    loader,
    options.fetchers,
    manifest,
    options.urlOf,
  );
  const sky = hdriFile(definition.sky.hdri, manifest);
  // The sky first, and nothing put in before its light is made: the PMREM's
  // moment then comes with little else on the GPU (worlds/budget.ts, moments).
  const fetchers = options.fetchers;
  const prepareSky =
    options.prepareSky ??
    ((image: FloatImage) =>
      Promise.resolve(
        skyParts({
          image,
          exposure: definition.sky.exposure,
          maxFace: Infinity,
          seen: definition.sky.seen,
        }),
      ));
  if (fetchers) {
    loader.requestFirst(
      sky.stored,
      () => fetchers.hdri(urlOf(sky.stored)).then(prepareSky),
      (parts) => options.onSky?.(parts),
    );
  }
  definition.build(builder);
  for (const plan of definition.props) builder.prop(plan);

  builder.files.add(sky.stored);
  // Its sounds are its files too: prefetched, counted and packed with the rest (ADR-034).
  for (const file of recordingsOf(definition.sound, manifest)) builder.files.add(file.stored);

  const towardsSun = sunOf(definition, manifest);
  const sun = makeSun(
    towardsSun,
    definition.sun.colour,
    definition.sun.intensity,
    true,
    definition.shadow,
  );
  builder.root.add(sun, sun.target);
  const halos = new HaloField(builder.halos.slice(0, HALOS_PER_WORLD), 'world-halos', false);
  halos.maxSize = 60;
  builder.root.add(halos.mesh);
  // The world's see-through things -- smoke, ash, glow, water foam -- are far
  // off, but a sprite cloud's own position is its centre, which may sort it
  // in front of a lesson's badge or name and paint over it. Drawn first, the
  // world's never cover the lesson's; among themselves they keep their order.
  builder.root.traverse((node) => {
    const material = (node as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
    const seeThrough = Array.isArray(material)
      ? material.some((m) => m.transparent)
      : material?.transparent === true;
    if (seeThrough) node.renderOrder -= WORLD_FIRST;
  });

  return {
    definition,
    builder,
    root: builder.root,
    sun,
    halos,
    towardsSun,
    loader,
    update(dt, elapsed, camera, held = false) {
      builder.update(dt, elapsed, camera, held);
      sun.intensity = definition.sun.intensity * builder.light.sun;
      halos.update(camera, elapsed);
    },
    setStates(states, animate) {
      for (const [name, state] of states) builder.features.get(name)?.set(state, animate);
    },
    dispose() {
      loader.dispose();
      halos.dispose();
      builder.root.removeFromParent();
      const textures = new Set<THREE.Texture>();
      builder.root.traverse((node) => {
        // Instanced props hold their instance matrices (and colours) on the GPU.
        if (node instanceof THREE.InstancedMesh) node.dispose();
        const drawn = node as THREE.Mesh<THREE.BufferGeometry, THREE.Material | THREE.Material[]>;
        if (!drawn.geometry) return;
        drawn.geometry.dispose();
        for (const material of Array.isArray(drawn.material) ? drawn.material : [drawn.material]) {
          for (const value of [
            ...(Object.values(material) as unknown[]),
            // A texture only a patched shader holds (the grass overlay, world.ts).
            ...(Object.values(material.userData) as unknown[]),
          ]) {
            if (value instanceof THREE.Texture) textures.add(value as THREE.Texture);
          }
          if (material instanceof THREE.ShaderMaterial) {
            for (const uniform of Object.values(material.uniforms)) {
              const value = uniform.value as unknown;
              if (value instanceof THREE.Texture) textures.add(value as THREE.Texture);
            }
          }
          material.dispose();
        }
      });
      for (const texture of textures) texture.dispose();
      sun.shadow.map?.dispose();
    },
  };
}
